import { useEffect, useRef, useCallback } from "react";

const IDLE_MS = 30 * 60 * 1000; // 30 minutes
const EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "mousemove"];
const THROTTLE_MS = 60_000; // only update timestamp once per minute

/**
 * Logs the user out after 30 minutes of inactivity.
 * Tracks mouse, keyboard, touch, and scroll events.
 * Also detects idle across tabs via localStorage timestamp.
 */
export function useIdleTimeout(onTimeout, active = true) {
  const timerRef = useRef(null);
  const lastRef = useRef(Date.now());

  const reset = useCallback(() => {
    const now = Date.now();
    // Throttle localStorage writes
    if (now - lastRef.current > THROTTLE_MS) {
      localStorage.setItem("vg-last-active", String(now));
      lastRef.current = now;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(onTimeout, IDLE_MS);
  }, [onTimeout]);

  useEffect(() => {
    if (!active) return;

    // Check if already idle from a previous session / other tab
    const stored = parseInt(localStorage.getItem("vg-last-active") || "0", 10);
    if (stored && Date.now() - stored > IDLE_MS) {
      onTimeout();
      return;
    }

    // Set initial timestamp
    localStorage.setItem("vg-last-active", String(Date.now()));
    lastRef.current = Date.now();

    // Start timer
    timerRef.current = setTimeout(onTimeout, IDLE_MS);

    // Listen to activity events
    EVENTS.forEach(e => window.addEventListener(e, reset, { passive: true }));

    // Listen to visibility changes (tab focus)
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Check elapsed time when tab becomes visible again
        const last = parseInt(localStorage.getItem("vg-last-active") || "0", 10);
        if (last && Date.now() - last > IDLE_MS) {
          onTimeout();
        } else {
          reset();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(timerRef.current);
      EVENTS.forEach(e => window.removeEventListener(e, reset));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active, onTimeout, reset]);
}
